// Pure axis & gradient compute for the live CPU / MEM metrics chart.
//
// Both inputs and outputs are plain data so the consumer (job-metrics-chart
// or runner-metrics-chart) keeps the d3 mutation code in one place while
// the math sits behind a pure boundary that's exercised in isolation.

import type { ScaleLinear } from "d3-scale";
import {
  cpuRegionScale,
  memRegionScale,
  pointX,
  seriesMax,
  type ChartPoint,
  type Rgb,
} from "./job-metrics-chart";

export type AxisScale = ScaleLinear<number, number>;

export interface MetricsDims {
  readonly w: number;
  readonly h: number;
}

export interface MetricsAxes {
  /** Time/sample index → x coord. `xScale(i)` returns the centre of slot i. */
  readonly xScale: (i: number) => number;
  /** Sample value (%) → y for the MEM region (lower half). */
  readonly memScale: AxisScale;
  /** Sample value (%) → y for the CPU region (upper half). */
  readonly cpuScale: AxisScale;
  /** Observed max values used to derive the two scale domains. */
  readonly cpuMax: number;
  readonly memMax: number;
  /** Integer tick positions along x — 0 .. n-1 (one per chart sample). */
  readonly ticks: ReadonlyArray<number>;
}

/**
 * Pure scale/tick compute. Every input is a plain data shape; every
 * output is a thin closure over scaleLinear — no DOM, no d3-selection.
 */
export const computeAxisScales = (
  chart: ReadonlyArray<ChartPoint>,
  dims: MetricsDims,
): MetricsAxes => {
  const n = chart.length;
  const cpuMax = seriesMax(chart, "cpuPct");
  const memMax = seriesMax(chart, "memPct");
  return {
    xScale: (i) => pointX(i, n, dims.w),
    cpuScale: cpuRegionScale(cpuMax, dims.h),
    memScale: memRegionScale(memMax, dims.h),
    cpuMax,
    memMax,
    ticks: Array.from({ length: n }, (_, i) => i),
  };
};

/** Theme tuple consumed by `buildGradientStops`. */
export interface MetricsTheme {
  readonly info: Rgb;
  readonly warning: Rgb;
  readonly danger: Rgb;
}

/** Ready-to-bind CSS gradient stops for the CPU and MEM color ramps. */
export interface GradientStops {
  readonly cpu: ReadonlyArray<Rgb>;
  readonly mem: ReadonlyArray<Rgb>;
}

/**
 * Three-stop gradient per series. CPU goes blue→warning→danger; MEM
 * starts on warning so the two stacked regions stay distinguishable
 * when they overlap on value.
 */
export const buildGradientStops = (theme: MetricsTheme): GradientStops => ({
  cpu: [theme.info, theme.warning, theme.danger],
  mem: [theme.warning, theme.warning, theme.danger],
});
